import { useEffect } from "react";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

import { SelectButton, SelectButtonChangeEvent } from "primereact/selectbutton";
import { MultiSelect } from "primereact/multiselect";
import { Message } from "primereact/message";

import { EvaluateRequestStatus } from "@/api/activities";
import { useEvaluateRequests } from "@/context/EvaluateRequestsContext";
import { useActivityAssignments } from "@/hooks/useActivityAssignments";
import { ActivityRequestCard } from "@/components/champion/ActivityRequests/ActivityRequestCard";
import { ActivityRequestCardSkeleton } from "@/components/champion/ActivityRequests/ActivityRequestCardSkeleton";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_FILTERS = [
  { label: "All", value: "all" },
  { label: "Submitted", value: "submitted" },
  { label: "In Progress", value: "in_progress" },
  { label: "Failed", value: "failed" },
  { label: "Completed", value: "passed" },
];

type StatusFilterValue = "all" | EvaluateRequestStatus;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const EvaluateRequests = () => {
  useBreadcrumb({
    label: "Evaluation Requests",
    route: AdminAppRoutes.routes.champions.routes.evaluateRequests.route,
    display: "none",
    resetTrail: true,
  });

  const {
    allActivities,
    loading,
    loadingOther,
    loadedStatuses,
    error,
    loadEvaluateRequests,
    filters,
    setFilters,
  } = useEvaluateRequests();

  const { selectedStatus, selectedAdmins, selectedUsers } = filters;

  const { admins, activityAssignments, handleAssignmentChange } =
    useActivityAssignments(allActivities);

  useEffect(() => {
    void loadEvaluateRequests();
  }, [loadEvaluateRequests]);

  const setSelectedStatus = (value: StatusFilterValue) => {
    setFilters(prev => ({ ...prev, selectedStatus: value }));
  };
  const setSelectedAdmins = (value: string[]) =>
    setFilters(prev => ({ ...prev, selectedAdmins: value }));
  const setSelectedUsers = (value: string[]) =>
    setFilters(prev => ({ ...prev, selectedUsers: value }));

  const filteredActivities = allActivities.filter(activity => {
    if (selectedStatus !== "all" && activity.requestStatus !== selectedStatus) return false;
    if (selectedUsers.length > 0 && !selectedUsers.includes(activity.userName)) return false;
    if (
      selectedAdmins.length > 0 &&
      !(activityAssignments[activity.id] ?? []).some(id => selectedAdmins.includes(id))
    ) {
      return false;
    }
    return true;
  });

  const uniqueUsers = [...new Set(allActivities.map(a => a.userName).filter(Boolean))].sort();
  const userOptions = uniqueUsers.map(name => ({ label: name, value: name }));

  // Show skeletons while loading initial data, or while the selected status tab hasn't loaded yet
  const showSkeleton =
    loading ||
    (loadingOther &&
      selectedStatus !== "all" &&
      !loadedStatuses.has(selectedStatus as EvaluateRequestStatus));

  return (
    <div className="mt-8">
      <div className="mb-4">
        <h1>Evaluation Requests</h1>
      </div>
      <div className="mb-6">
        <div className="flex gap-4 items-end flex-wrap">
          <div className="flex flex-col gap-1">
            <label htmlFor="user-filter" className="body-xs text-muted">
              User
            </label>
            <MultiSelect
              id="user-filter"
              value={selectedUsers}
              onChange={e => setSelectedUsers(e.value as string[])}
              options={userOptions}
              optionLabel="label"
              optionValue="value"
              placeholder="All Users"
              style={{ minWidth: "200px" }}
              display="chip"
              filter
              showSelectAll={false}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="admin-filter" className="body-xs text-muted">
              Admin Assigned
            </label>
            <MultiSelect
              id="admin-filter"
              value={selectedAdmins}
              onChange={e => setSelectedAdmins(e.value as string[])}
              options={admins}
              optionLabel="label"
              optionValue="value"
              placeholder="Anyone"
              style={{ minWidth: "200px" }}
              display="chip"
              filter
              showSelectAll={false}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="evaluate-status-filter" className="body-xs text-muted">
              Status
            </label>
            <SelectButton
              id="evaluate-status-filter"
              value={selectedStatus}
              onChange={(e: SelectButtonChangeEvent) => {
                if (e.value !== null) setSelectedStatus(e.value as StatusFilterValue);
              }}
              options={STATUS_FILTERS}
              optionLabel="label"
              optionValue="value"
              pt={{ root: { "data-testid": "evaluate-requests-status-filter" } }}
            />
          </div>
        </div>
      </div>

      {showSkeleton ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "1.5rem",
          }}
        >
          {[...Array(6)].map((_, i) => (
            <ActivityRequestCardSkeleton key={i} />
          ))}
        </div>
      ) : error ? (
        <Message severity="error" text={error} className="my-4" />
      ) : filteredActivities.length === 0 ? (
        <Message
          severity="info"
          text={
            selectedStatus === "all"
              ? "No evaluation requests found."
              : `No ${STATUS_FILTERS.find(o => o.value === selectedStatus)?.label ?? selectedStatus} evaluation requests found.`
          }
          className="my-4"
          pt={{ root: { role: "status" } }}
        />
      ) : (
        <div
          data-testid="evaluate-requests-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "1.5rem",
          }}
        >
          {filteredActivities.map(activity => (
            <ActivityRequestCard
              key={activity.id}
              activity={activity}
              admins={admins}
              assignedAdminIds={activityAssignments[activity.id] ?? []}
              onAssignmentChange={handleAssignmentChange}
              requestType="evaluate"
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default EvaluateRequests;
